import React, { useState } from "react";
import { X, Crown, Sparkles, ShoppingBag, Lock, Check, AlertCircle, ExternalLink, UserPlus, Mail, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";

// Public site: paid tiers and Video Store are hidden until re-enabled.
const SHOW_PAID_TIERS = false;
const SHOW_VIDEO_STORE = false;

const JOIN_FREE_URL = "/members/login";

// Lucide has no brand logos, so platforms use monogram marks.
const PLATFORMS = [
  { name: "OnlyFans", mark: "OF", href: "https://onlyfans.com/giopilos", color: "#38bdf8" },
  { name: "JustForFans", mark: "JFF", href: "https://justfor.fans/giopilos", color: "#f472b6" },
  { name: "FapHouse", mark: "FH", href: "https://faphouse.com/giopilos", color: "#a78bfa" },
];

const NEON_CSS = `
@keyframes mm-gradient-shift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
.mm-animated { background-size: 200% 200%; animation: mm-gradient-shift 6s ease-in-out infinite; }
.mm-cta { box-shadow: 0 0 18px var(--mm-glow, rgba(232,121,249,0.5)); transition: box-shadow 0.3s ease, transform 0.2s ease, filter 0.3s ease; }
.mm-cta:hover { transform: translateY(-2px); filter: brightness(1.1); box-shadow: 0 0 32px var(--mm-glow, rgba(232,121,249,0.8)), 0 0 64px rgba(34,211,238,0.3); }
.mm-cta:focus-visible { outline: 3px solid #f0abfc; outline-offset: 4px; }
@media (prefers-reduced-motion: reduce) { .mm-animated { animation: none; } .mm-cta, .mm-cta:hover { transition: none; transform: none; } }
`;

export default function MembershipModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    agreeToTerms: false
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.name) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.agreeToTerms) {
      toast.error("Please agree to the Terms of Service");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/api/members/auth/register", {
        email: formData.email,
        password: formData.password,
        name: formData.name
      });
      
      if (res.data.token) {
        localStorage.setItem("memberToken", res.data.token);
        localStorage.setItem("memberUser", JSON.stringify(res.data.user));
        toast.success("Welcome! Your free account has been created 🎉");
        onClose();
        navigate("/members/free");
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.detail || "Registration failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto backdrop-blur-xl"
      style={{ background: "rgba(0,0,0,0.95)" }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-6xl my-8 rounded-3xl border border-white/10"
        style={{ background: "#000000" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative overflow-hidden border-b border-white/10 p-6 sm:p-10">
          {/* Neon glow background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500 rounded-full blur-3xl" />
          </div>

          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h2 className="text-3xl sm:text-5xl font-black leading-tight text-white mb-3">
                Premium gay adult content <span className="text-pink-400">•</span> Free access available now
              </h2>
              <p className="text-lg sm:text-xl font-semibold text-purple-300">
                Join Free or Become a Member
              </p>
            </div>

            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition text-zinc-400 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 sm:p-10">
          <style>{NEON_CSS}</style>
          
          {/* Tab Switcher */}
          <div className="flex justify-center gap-3 mb-8">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-6 py-3 rounded-xl font-bold transition ${
                activeTab === "overview"
                  ? "text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              style={activeTab === "overview" ? {
                background: "linear-gradient(90deg,#22d3ee,#e879f9)",
                boxShadow: "0 0 20px rgba(232,121,249,0.5)"
              } : {}}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("signup")}
              className={`px-6 py-3 rounded-xl font-bold transition ${
                activeTab === "signup"
                  ? "text-white"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
              style={activeTab === "signup" ? {
                background: "linear-gradient(90deg,#e879f9,#f472b6)",
                boxShadow: "0 0 20px rgba(244,114,182,0.5)"
              } : {}}
            >
              Sign Up Now
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className={`grid grid-cols-1 ${SHOW_PAID_TIERS ? "md:grid-cols-3" : "max-w-2xl mx-auto"} gap-6 mb-8`}>
              {/* FREE TIER - Overview Card */}
              <div className="relative rounded-3xl border p-6 sm:p-10"
                style={{
                  borderColor: "rgba(232,121,249,0.5)",
                  background: "linear-gradient(160deg, rgba(24,8,38,0.95), rgba(4,4,10,0.98))",
                  boxShadow: "0 0 0 1px rgba(232,121,249,0.15), 0 0 32px rgba(168,85,247,0.35), 0 0 80px rgba(236,72,153,0.15)",
                }}>
                <div className="absolute inset-x-6 -top-px h-[3px] rounded-full mm-animated"
                  style={{ backgroundImage: "linear-gradient(90deg,#22d3ee,#e879f9,#f472b6,#22d3ee)" }} />
                <span className="absolute -top-4 right-6 px-5 py-1.5 rounded-full text-xs sm:text-sm font-black tracking-[0.25em] text-black"
                  style={{ background: "linear-gradient(90deg,#22d3ee,#e879f9)", boxShadow: "0 0 18px rgba(232,121,249,0.85)" }}>
                  FREE
                </span>

                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-fuchsia-400/40"
                    style={{ background: "rgba(232,121,249,0.12)" }}>
                    <Crown className="text-fuchsia-300" size={28} />
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-black text-white">Free Member</h3>
                </div>
                <p className="text-base sm:text-lg text-zinc-300 mb-6">Browse trailers & previews</p>
                <div className="mb-8 text-6xl sm:text-8xl font-black leading-none"
                  style={{
                    background: "linear-gradient(90deg,#22d3ee,#e879f9,#f472b6)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    filter: "drop-shadow(0 0 18px rgba(232,121,249,0.55))",
                  }}>
                  $0
                </div>

                <ul className="space-y-3 mb-8">
                  {["Video trailers & previews", "Photo gallery previews", "Public posts & updates", "Direct messaging"].map((feature) => (
                    <li key={feature} className="flex items-center gap-4 rounded-xl border border-fuchsia-400/20 bg-white/[0.03] px-4 py-4 text-base sm:text-lg text-zinc-100">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ background: "rgba(232,121,249,0.15)", boxShadow: "0 0 14px rgba(232,121,249,0.5)" }}>
                        <Check className="text-fuchsia-300" size={18} strokeWidth={3} />
                      </span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setActiveTab("signup")}
                  className="mm-cta flex w-full min-h-[56px] items-center justify-center px-8 py-4 rounded-2xl text-lg font-black uppercase tracking-wider text-black"
                  style={{ background: "linear-gradient(90deg,#22d3ee,#e879f9 60%,#f472b6)", "--mm-glow": "rgba(232,121,249,0.7)" }}
                >
                  Join Free
                </button>
              </div>
            </div>
          )}

          {/* Sign Up Tab */}
          {activeTab === "signup" && (
            <div className="max-w-2xl mx-auto mb-8">
            {/* FREE TIER - Signup Form */}
            <div className="relative rounded-3xl border p-6 sm:p-10"
              style={{
                borderColor: "rgba(232,121,249,0.5)",
                background: "linear-gradient(160deg, rgba(24,8,38,0.95), rgba(4,4,10,0.98))",
                boxShadow: "0 0 0 1px rgba(232,121,249,0.15), 0 0 32px rgba(168,85,247,0.35), 0 0 80px rgba(236,72,153,0.15)",
              }}>
              <div className="absolute inset-x-6 -top-px h-[3px] rounded-full mm-animated"
                style={{ backgroundImage: "linear-gradient(90deg,#22d3ee,#e879f9,#f472b6,#22d3ee)" }} />
              <span className="absolute -top-4 right-6 px-5 py-1.5 rounded-full text-xs sm:text-sm font-black tracking-[0.25em] text-black"
                style={{ background: "linear-gradient(90deg,#22d3ee,#e879f9)", boxShadow: "0 0 18px rgba(232,121,249,0.85)" }}>
                FREE
              </span>

              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
                  style={{ background: "rgba(232,121,249,0.1)", border: "1px solid rgba(232,121,249,0.3)" }}>
                  <Sparkles size={16} style={{ color: "#e879f9" }} />
                  <span className="text-sm font-semibold" style={{ color: "#e879f9" }}>FREE MEMBERSHIP</span>
                </div>
                <h3 className="text-4xl font-black mb-2 text-white">Sign Up Free</h3>
                <p className="text-zinc-400">No credit card required • Access free content instantly</p>
              </div>

              {/* Registration Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    <User className="inline mr-2" size={16} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    required
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-fuchsia-500 transition"
                    style={{ background: "rgba(0,0,0,0.3)", borderColor: "rgba(113,113,122,0.3)" }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    <Mail className="inline mr-2" size={16} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-fuchsia-500 transition"
                    style={{ background: "rgba(0,0,0,0.3)", borderColor: "rgba(113,113,122,0.3)" }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    <Lock className="inline mr-2" size={16} />
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 8 characters"
                    required
                    className="w-full px-4 py-3 rounded-lg border text-white placeholder-zinc-500 focus:outline-none focus:border-fuchsia-500 transition"
                    style={{ background: "rgba(0,0,0,0.3)", borderColor: "rgba(113,113,122,0.3)" }}
                  />
                  <p className="text-xs text-zinc-500 mt-1">Minimum 8 characters</p>
                </div>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    className="mt-1"
                    style={{ accentColor: "#e879f9" }}
                  />
                  <label className="text-sm text-zinc-400">
                    I agree to the <a href="/legal" className="text-fuchsia-400 hover:underline">Terms of Service</a> and <a href="/legal" className="text-fuchsia-400 hover:underline">Privacy Policy</a>. I confirm I am 18+ years old.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl font-bold text-lg transition hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{
                    background: loading ? "#71717a" : "linear-gradient(135deg,#e879f9 0%,#f472b6 100%)",
                    color: "#ffffff"
                  }}
                >
                  {loading ? (
                    "Creating Account..."
                  ) : (
                    <>
                      <UserPlus size={20} />
                      Create Free Account
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-zinc-400">
                  Already have an account? <a href="/members/login" className="text-fuchsia-400 hover:underline font-medium">Sign In</a>
                </p>
              </div>

              {/* Benefits List */}
              <div className="mt-8 backdrop-blur-xl bg-white/5 border border-white/10 rounded-xl p-6">
                <h4 className="font-bold text-white mb-4">What's Included (Free):</h4>
                <ul className="space-y-2 text-sm text-zinc-300">
                  {[
                    "Access to free preview content and galleries",
                    "Daily free posts and updates",
                    "Community access and member features",
                    "No credit card required"
                  ].map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="text-fuchsia-400 shrink-0 mt-0.5" size={18} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            </div>
          )}

          {/* Paid Tiers - Show in both tabs */}
          {activeTab === "overview" && (
            <>
            {SHOW_PAID_TIERS && (<>
            {/* MEMBER TIER - Coming Soon */}
            <div className="relative rounded-2xl border border-purple-500/30 backdrop-blur-xl p-6"
              style={{ background: "rgba(0, 0, 0, 0.6)" }}>
              {/* Coming Soon Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-purple-500/20 border border-purple-500/50 rounded-full">
                <span className="text-xs font-bold text-purple-300 uppercase tracking-wider">Coming Soon</span>
              </div>

              <div className="mb-6">
                <div className="w-12 h-12 bg-purple-500/20 border border-purple-500/30 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="text-purple-400" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Basic Member</h3>
                <p className="text-sm text-zinc-500 mb-4">Full exclusive content</p>
                <div className="text-3xl font-bold text-white mb-2">$9.99<span className="text-sm text-zinc-500">/mo</span></div>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Everything in Free +</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Full-length videos</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Complete photo galleries</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Behind-the-scenes content</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-purple-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Priority DM responses</span>
                </li>
              </ul>

              <button
                disabled
                className="block w-full px-6 py-3 rounded-xl font-semibold text-center transition bg-purple-500/10 text-purple-400/50 border border-purple-500/20 cursor-not-allowed"
              >
                <Lock size={16} className="inline-block mr-2" />
                Payment System Updating
              </button>
            </div>

            {/* VIP TIER - Coming Soon */}
            <div className="relative rounded-2xl border border-pink-500/30 backdrop-blur-xl p-6"
              style={{ background: "rgba(0, 0, 0, 0.6)" }}>
              {/* Coming Soon Badge */}
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/50 rounded-full">
                <span className="text-xs font-bold text-pink-300 uppercase tracking-wider">Coming Soon</span>
              </div>

              <div className="mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-pink-500/30 rounded-xl flex items-center justify-center mb-4">
                  <Crown className="text-pink-400" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">VIP</h3>
                <p className="text-sm text-zinc-500 mb-4">Ultimate exclusive access</p>
                <div className="text-3xl font-bold text-white mb-2">$19.99<span className="text-sm text-zinc-500">/mo</span></div>
              </div>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-pink-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Everything in Member +</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-pink-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">VIP-only content & videos</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-pink-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Store discounts (20% off)</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-pink-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Early access to new releases</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <Check className="text-pink-400 flex-shrink-0 mt-0.5" size={16} />
                  <span className="text-zinc-300">Personal video requests</span>
                </li>
              </ul>

              <button
                disabled
                className="block w-full px-6 py-3 rounded-xl font-semibold text-center transition bg-pink-500/10 text-pink-400/50 border border-pink-500/20 cursor-not-allowed"
              >
                <Lock size={16} className="inline-block mr-2" />
                Payment System Updating
              </button>
            </div>
            </>)}
            </>
          )}

          {SHOW_VIDEO_STORE && (<>
          {/* STORE - Under Maintenance */}
          <div className="relative rounded-2xl border border-orange-500/30 backdrop-blur-xl p-8"
            style={{ background: "rgba(0, 0, 0, 0.6)" }}>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Icon */}
              <div className="w-16 h-16 bg-orange-500/10 border border-orange-500/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="text-orange-400" size={32} />
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-white">Video Store</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-500/20 border border-orange-500/40 rounded-full">
                    <AlertCircle size={14} className="text-orange-400" />
                    <span className="text-xs font-bold text-orange-300 uppercase tracking-wider">Under Maintenance</span>
                  </span>
                </div>
                <p className="text-zinc-400 mb-4">
                  Purchase premium videos directly. Pay once, keep forever. Currently updating payment infrastructure.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <li className="flex items-center gap-2 text-zinc-500">
                    <Check size={14} className="text-zinc-600" />
                    <span>4K full-length videos</span>
                  </li>
                  <li className="flex items-center gap-2 text-zinc-500">
                    <Check size={14} className="text-zinc-600" />
                    <span>Instant download access</span>
                  </li>
                  <li className="flex items-center gap-2 text-zinc-500">
                    <Check size={14} className="text-zinc-600" />
                    <span>No recurring charges</span>
                  </li>
                  <li className="flex items-center gap-2 text-zinc-500">
                    <Check size={14} className="text-zinc-600" />
                    <span>Lifetime ownership</span>
                  </li>
                </ul>
              </div>

              {/* Disabled button */}
              <button
                disabled
                className="px-8 py-3 rounded-xl font-semibold transition bg-orange-500/10 text-orange-400/50 border border-orange-500/20 cursor-not-allowed whitespace-nowrap"
              >
                <Lock size={16} className="inline-block mr-2" />
                Temporarily Unavailable
              </button>
            </div>
          </div>
          </>)}

          {/* Common footer sections - Show in both tabs */}
          <div className="mt-8">
            <div className="flex justify-center">
              <div aria-disabled="true"
                className="flex items-center gap-4 rounded-2xl border border-violet-400/20 bg-white/[0.02] px-6 py-4 select-none cursor-not-allowed">
                <span className="w-11 h-11 rounded-xl flex items-center justify-center border border-violet-400/30"
                  style={{ background: "rgba(167,139,250,0.08)" }}>
                  <Lock className="text-violet-300/70" size={20} />
                </span>
                <p className="text-base sm:text-lg font-semibold text-violet-200/80">Premium — Coming Soon</p>
              </div>
            </div>

            <section className="mt-12 pt-10 border-t border-white/10 text-center" aria-labelledby="immediate-access-heading">
              <p className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-cyan-300 mb-3">
                Immediate Subscription Access
              </p>
              <h3 id="immediate-access-heading" className="text-xl sm:text-2xl font-bold text-white mb-8">
                For immediate subscription access, visit our platforms:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {PLATFORMS.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mm-cta flex min-h-[72px] items-center justify-between gap-3 rounded-2xl border px-5 py-4 font-bold text-white"
                    style={{
                      "--mm-glow": `${platform.color}88`,
                      borderColor: `${platform.color}66`,
                      background: `${platform.color}14`,
                    }}
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black text-black"
                        style={{ background: platform.color, boxShadow: `0 0 14px ${platform.color}` }}>
                        {platform.mark}
                      </span>
                      <span className="text-lg">{platform.name}</span>
                    </span>
                    <ExternalLink size={18} style={{ color: platform.color }} />
                  </a>
                ))}
              </div>
            </section>

            <p className="text-xs text-zinc-600 mt-8 text-center">
              18+ only • Payment system transition in progress • Check back soon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
